import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useFinancial } from '../context/FinancialContext';

const { FiX, FiTarget, FiPlus, FiMinus, FiPercent, FiBookOpen, FiAward, FiUsers, FiUser, FiCalendar, FiInfo, FiClock } = FiIcons;

const goalTypes = [
  'Child Education',
  'Higher Education',
  'House Purchase',
  'Car Purchase',
  'Wedding',
  'Vacation/Travel',
  'Emergency Fund',
  'Business Investment',
  'Property Investment',
  'Retirement Top-up',
  'Healthcare Fund',
  'Home Renovation',
  'Debt Clearance',
  'Personal Goal',
  'Other'
];

const goalCategories = {
  'Child Education': { inflationType: 'education', priority: 'high' },
  'Higher Education': { inflationType: 'education', priority: 'high' },
  'House Purchase': { inflationType: 'general', priority: 'high' },
  'Car Purchase': { inflationType: 'general', priority: 'medium' },
  'Wedding': { inflationType: 'general', priority: 'high' },
  'Vacation/Travel': { inflationType: 'general', priority: 'low' },
  'Emergency Fund': { inflationType: 'general', priority: 'critical' },
  'Business Investment': { inflationType: 'general', priority: 'medium' },
  'Property Investment': { inflationType: 'general', priority: 'medium' },
  'Retirement Top-up': { inflationType: 'general', priority: 'high' },
  'Healthcare Fund': { inflationType: 'medical', priority: 'high' },
  'Home Renovation': { inflationType: 'general', priority: 'low' },
  'Debt Clearance': { inflationType: 'general', priority: 'critical' },
  'Personal Goal': { inflationType: 'general', priority: 'medium' },
  'Other': { inflationType: 'general', priority: 'medium' }
};

const whatIfPercentageOptions = [20, 40, 60, 80, 100];

const educationClasses = [
  'Pre-School',
  'Class 1',
  'Class 2',
  'Class 3',
  'Class 4',
  'Class 5',
  'Class 6',
  'Class 7',
  'Class 8',
  'Class 9',
  'Class 10',
  'Class 11',
  'Class 12'
];

const educationTargets = [
  { id: 'iit', name: 'IITs (Engineering)', baseExpense: 1500000, inflationRate: 10, category: 'Engineering', duration: '4 Years' },
  { id: 'nit', name: 'NITs (Engineering)', baseExpense: 1000000, inflationRate: 9, category: 'Engineering', duration: '4 Years' },
  { id: 'bits', name: 'BITS (Engineering)', baseExpense: 2400000, inflationRate: 10, category: 'Engineering', duration: '4 Years' },
  { id: 'iiit', name: 'IIITs (Engineering)', baseExpense: 1600000, inflationRate: 9, category: 'Engineering', duration: '4 Years' },
  { id: 'vit_srm', name: 'VIT/SRM (Engineering)', baseExpense: 1400000, inflationRate: 8, category: 'Engineering', duration: '4 Years' },
  { id: 'manipal', name: 'Manipal (Engineering)', baseExpense: 2200000, inflationRate: 9, category: 'Engineering', duration: '4 Years' },
  { id: 'thapar', name: 'Thapar (Engineering)', baseExpense: 1800000, inflationRate: 8, category: 'Engineering', duration: '4 Years' },
  { id: 'govt_mbbs', name: 'Govt MBBS', baseExpense: 1000000, inflationRate: 11, category: 'Medical', duration: '5.5 Years' },
  { id: 'private_mbbs', name: 'Private MBBS', baseExpense: 7000000, inflationRate: 12, category: 'Medical', duration: '5.5 Years' },
  { id: 'abroad_med', name: 'Medical Abroad (Ukraine/China)', baseExpense: 4000000, inflationRate: 10, category: 'Medical', duration: '5.5 Years' },
  { id: 'us_ug', name: 'US (Undergraduate)', baseExpense: 7500000, inflationRate: 11, category: 'Study Abroad', duration: '4 Years' },
  { id: 'uk_ug', name: 'UK (Undergraduate)', baseExpense: 7000000, inflationRate: 11, category: 'Study Abroad', duration: '4 Years' },
  { id: 'canada_ug', name: 'Canada (Undergraduate)', baseExpense: 6000000, inflationRate: 10, category: 'Study Abroad', duration: '4 Years' },
  { id: 'germany_ug', name: 'Germany (Undergraduate)', baseExpense: 3000000, inflationRate: 9, category: 'Study Abroad', duration: '4 Years' },
  { id: 'du', name: 'Delhi University (Arts/Commerce)', baseExpense: 800000, inflationRate: 8, category: 'Liberal Arts/Commerce', duration: '3 Years' },
  { id: 'ashoka', name: 'Ashoka University (Liberal Arts)', baseExpense: 2500000, inflationRate: 10, category: 'Liberal Arts/Commerce', duration: '4 Years' },
  { id: 'christ', name: 'Christ University (Arts/Commerce)', baseExpense: 1200000, inflationRate: 9, category: 'Liberal Arts/Commerce', duration: '3 Years' },
  { id: 'nlu', name: 'NLUs (Law)', baseExpense: 1500000, inflationRate: 10, category: 'Law', duration: '5 Years' },
  { id: 'symbiosis_law', name: 'Symbiosis Law School', baseExpense: 2500000, inflationRate: 9, category: 'Law', duration: '5 Years' },
  { id: 'private_law', name: 'Private Law Colleges', baseExpense: 2000000, inflationRate: 9, category: 'Law', duration: '5 Years' },
  { id: 'iim', name: 'IIMs (MBA)', baseExpense: 2500000, inflationRate: 10, category: 'MBA', duration: '2 Years' },
  { id: 'isb_xlri', name: 'ISB/XLRI (MBA)', baseExpense: 3500000, inflationRate: 10, category: 'MBA', duration: '2 Years' },
  { id: 'abroad_mba', name: 'MBA Abroad (US/UK)', baseExpense: 6000000, inflationRate: 11, category: 'MBA', duration: '2 Years' },
  { id: 'iit_mtech', name: 'IITs/IISc (MSc/M.Tech)', baseExpense: 1000000, inflationRate: 9, category: 'MSc/M.Tech', duration: '2 Years' },
  { id: 'europe_masters', name: 'Europe (MSc/Masters)', baseExpense: 2000000, inflationRate: 10, category: 'MSc/M.Tech', duration: '2 Years' },
  { id: 'iti', name: 'ITI (Vocational)', baseExpense: 200000, inflationRate: 7, category: 'Vocational/Diplomas', duration: '1-3 Years' },
  { id: 'private_diploma', name: 'Private Diplomas', baseExpense: 1000000, inflationRate: 8, category: 'Vocational/Diplomas', duration: '1-3 Years' },
  { id: 'phd_india', name: 'PhD in India (Stipend-covered)', baseExpense: 500000, inflationRate: 8, category: 'PhD/Research', duration: '3-5 Years' },
  { id: 'phd_abroad', name: 'PhD Abroad (Partially Funded)', baseExpense: 5000000, inflationRate: 10, category: 'PhD/Research', duration: '3-5 Years' },
  { id: 'other_education', name: 'Other Higher Education', baseExpense: 1000000, inflationRate: 8, category: 'Other', duration: 'Varies' }
];

const getFirstTargetForCategory = (category) => {
  const target = educationTargets.find(t => t.category === category);
  return target ? target.id : 'iit';
};

function GoalForm({ goal, userProfile, onSubmit, onClose }) {
  const { state } = useFinancial();

  const [formData, setFormData] = useState({
    name: '',
    customGoalName: '',
    type: 'Lump Sum',
    timeHorizon: 5,
    goalAmount: 1000000,
    inflationRate: userProfile?.inflationRates?.general || 6,
    expectedReturn: userProfile?.returnExpectations?.mediumTerm || 10,
    taggedAssets: [],
    sipContribution: 0,
    lumpSumContribution: 0,
    whatIfPercentage: 40,
    useIncomePeriod: true,
    currentSchoolExpense: 0,
    childCount: 1,
    children: [{
      name: '',
      currentClass: 'Class 1',
      targetEducation: 'iit',
      yearlyFees: 0
    }],
    useEducationLoan: false,
    educationLoanPercentage: 50,
    customEducationCost: 0,
    educationTodayCost: 0,
    educationInflationRate: 10,
    educationYearsToTarget: 0,
    ...(goal || {})
  });

  const [showEducationPlanner, setShowEducationPlanner] = useState(formData.name === 'Child Education');
  const [educationCategory, setEducationCategory] = useState('Engineering');

  const activeIncomePeriod = userProfile.retirementAge - userProfile.currentAge;
  const goalTimeHorizon = formData.timeHorizon;
  const investmentHorizon = formData.useIncomePeriod
    ? Math.min(goalTimeHorizon, Math.max(0, activeIncomePeriod))
    : goalTimeHorizon;

  const getAssetOptions = () => {
    const options = [];
    const investments = userProfile?.existingInvestments || {};
    const assets = userProfile?.assets || {};

    const calculateTaggedAmount = (assetKey) => {
      let totalTagged = 0;
      state.goals.forEach(existingGoal => {
        if (existingGoal.id !== goal?.id && existingGoal.taggedAssets) {
          existingGoal.taggedAssets.forEach(taggedAsset => {
            if (taggedAsset.assetKey === assetKey) totalTagged += taggedAsset.amount || 0;
          });
        }
      });
      return totalTagged;
    };

    Object.entries(investments).forEach(([key, value]) => {
      if (value > 0) {
        const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
        const tagged = calculateTaggedAmount(key);
        const remaining = value - tagged;
        if (remaining > 0) options.push({ value: key, label, amount: value, remaining, tagged, category: 'investments' });
      }
    });

    Object.entries(assets).forEach(([key, value]) => {
      if (value > 0 && key !== 'vehicles' && key !== 'insurance') {
        const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
        const tagged = calculateTaggedAmount(key);
        const remaining = value - tagged;
        if (remaining > 0) options.push({ value: key, label, amount: value, remaining, tagged, category: 'assets' });
      }
    });

    return options;
  };

  const assetOptions = getAssetOptions();
  const totalAvailableAssets = assetOptions.reduce((sum, asset) => sum + asset.remaining, 0);

  useEffect(() => {
    let expectedReturn = userProfile?.returnExpectations?.mediumTerm || 10;
    if (goalTimeHorizon <= 3) expectedReturn = userProfile?.returnExpectations?.shortTerm || 7;
    else if (goalTimeHorizon >= 7) expectedReturn = userProfile?.returnExpectations?.longTerm || 12;

    setFormData(prev => ({ ...prev, expectedReturn }));
  }, [formData.timeHorizon, userProfile]);

  useEffect(() => {
    if (formData.name === 'Child Education') {
      setShowEducationPlanner(true);
      updateEducationGoalAmount();
    } else {
      setShowEducationPlanner(false);
    }
  }, [formData.name]);

  useEffect(() => {
    if (showEducationPlanner) updateEducationGoalAmount();
  }, [formData.children, educationCategory, formData.useEducationLoan, formData.educationLoanPercentage]);

  const handleInputChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleGoalTypeChange = (goalType) => {
    const category = goalCategories[goalType];
    let inflationRate = userProfile?.inflationRates?.general || 6;

    if (category?.inflationType === 'education') inflationRate = userProfile?.inflationRates?.education || 8;
    else if (category?.inflationType === 'medical') inflationRate = userProfile?.inflationRates?.medical || 10;

    setFormData(prev => ({
      ...prev,
      name: goalType,
      inflationRate,
      customGoalName: goalType === 'Other' ? prev.customGoalName : ''
    }));

    if (goalType === 'Child Education') setShowEducationPlanner(true);
    else setShowEducationPlanner(false);
  };

  const calculateYearsToEducation = (currentClass) => {
    const classIndex = educationClasses.indexOf(currentClass);
    if (classIndex === -1) return 10;
    return educationClasses.length - 1 - classIndex;
  };

  const handleEducationCategoryChange = (newCategory) => {
    const firstTargetId = getFirstTargetForCategory(newCategory);
    const resetChildren = formData.children.map(child => ({
      ...child,
      targetEducation: firstTargetId,
      yearlyFees: 0
    }));
    setEducationCategory(newCategory);
    setFormData(prev => ({ ...prev, children: resetChildren }));
  };

  const updateEducationGoalAmount = () => {
    if (!showEducationPlanner) return;

    let totalTodaysCost = 0;
    let maxTimeHorizon = 0;
    let avgInflationRate = 0;

    formData.children.forEach(child => {
      const yearsToEducation = calculateYearsToEducation(child.currentClass);
      maxTimeHorizon = Math.max(maxTimeHorizon, yearsToEducation);

      const selectedTarget = educationTargets.find(target => target.id === child.targetEducation);
      if (selectedTarget) {
        const todayCost = selectedTarget.baseExpense + (Number(child.yearlyFees) || 0);
        totalTodaysCost += todayCost;
        avgInflationRate += selectedTarget.inflationRate;
      }
    });

    avgInflationRate = formData.children.length > 0 ? avgInflationRate / formData.children.length : 8;

    const savingsTargetRatio = formData.useEducationLoan
      ? (1 - (formData.educationLoanPercentage || 50) / 100)
      : 1;

    const targetGoalAmount = Math.round(totalTodaysCost * savingsTargetRatio);

    setFormData(prev => ({
      ...prev,
      goalAmount: targetGoalAmount,
      inflationRate: avgInflationRate,
      timeHorizon: maxTimeHorizon,
      educationTodayCost: Math.round(totalTodaysCost),
      educationYearsToTarget: maxTimeHorizon,
      educationInflationRate: avgInflationRate,
    }));
  };

  const addTaggedAsset = () => {
    if (formData.taggedAssets.length < 3)
      setFormData(prev => ({ ...prev, taggedAssets: [...prev.taggedAssets, { assetKey: '', amount: 0, expectedReturn: 0 }] }));
  };

  const removeTaggedAsset = (index) =>
    setFormData(prev => ({ ...prev, taggedAssets: prev.taggedAssets.filter((_, i) => i !== index) }));

  const updateTaggedAsset = (index, field, value) =>
    setFormData(prev => ({
      ...prev,
      taggedAssets: prev.taggedAssets.map((asset, i) => i === index ? { ...asset, [field]: value } : asset)
    }));

  const handleAssetSelection = (index, assetKey) => {
    const selectedAsset = assetOptions.find(asset => asset.value === assetKey);
    if (selectedAsset) {
      updateTaggedAsset(index, 'assetKey', assetKey);
      updateTaggedAsset(index, 'amount', Math.min(selectedAsset.remaining, selectedAsset.amount));
    }
  };

  const handleChildCountChange = (count) => {
    const newCount = parseInt(count);
    if (newCount < 1 || newCount > 3) return;

    let updatedChildren = [...formData.children];
    const firstTargetId = getFirstTargetForCategory(educationCategory);

    if (newCount > updatedChildren.length) {
      while (updatedChildren.length < newCount)
        updatedChildren.push({ name: '', currentClass: 'Class 1', targetEducation: firstTargetId, yearlyFees: 0 });
    } else {
      updatedChildren = updatedChildren.slice(0, newCount);
    }

    setFormData(prev => ({ ...prev, childCount: newCount, children: updatedChildren }));
  };

  const handleChildDataChange = (index, field, value) => {
    const updatedChildren = formData.children.map((child, i) =>
      i === index ? { ...child, [field]: value } : child
    );
    setFormData(prev => ({ ...prev, children: updatedChildren }));
  };

  const calculateWhatIfScenario = () => {
    const futureValue = formData.goalAmount * Math.pow(1 + formData.inflationRate / 100, goalTimeHorizon);
    const whatIfMultiplier = 1 + (formData.whatIfPercentage / 100);
    const whatIfFutureValue = futureValue * whatIfMultiplier;

    const monthlyReturn = formData.expectedReturn / 100 / 12;
    const totalInvestmentMonths = investmentHorizon * 12;
    const growthGapYears = goalTimeHorizon - investmentHorizon;
    const gapAtInvestmentEnd = whatIfFutureValue / Math.pow(1 + formData.expectedReturn / 100, growthGapYears);

    let whatIfSIP = 0;
    if (gapAtInvestmentEnd > 0 && totalInvestmentMonths > 0) {
      if (monthlyReturn > 0) {
        whatIfSIP = gapAtInvestmentEnd / ((Math.pow(1 + monthlyReturn, totalInvestmentMonths) - 1) / monthlyReturn);
      } else {
        whatIfSIP = gapAtInvestmentEnd / totalInvestmentMonths;
      }
    }

    const whatIfLumpsum = whatIfFutureValue / Math.pow(1 + formData.expectedReturn / 100, goalTimeHorizon);

    return {
      standardFutureValue: futureValue,
      whatIfFutureValue,
      whatIfSIP,
      whatIfLumpsum,
      additionalAmount: whatIfFutureValue - futureValue
    };
  };

  const calculateProjectedSIP = () => {
    const futureValue = formData.goalAmount * Math.pow(1 + formData.inflationRate / 100, goalTimeHorizon);

    let taggedAssetsFutureValue = 0;
    formData.taggedAssets.forEach(asset => {
      if (asset.amount > 0 && asset.expectedReturn > 0) {
        taggedAssetsFutureValue += asset.amount * Math.pow(1 + asset.expectedReturn / 100, goalTimeHorizon);
      }
    });

    const remainingGap = Math.max(0, futureValue - taggedAssetsFutureValue);

    const growthGapYears = goalTimeHorizon - investmentHorizon;
    const gapAtInvestmentEnd = remainingGap / Math.pow(1 + formData.expectedReturn / 100, growthGapYears);

    const monthlyReturn = formData.expectedReturn / 100 / 12;
    const totalInvestmentMonths = investmentHorizon * 12;

    let requiredSIP = 0;
    if (gapAtInvestmentEnd > 0 && totalInvestmentMonths > 0) {
      if (monthlyReturn > 0) {
        requiredSIP = gapAtInvestmentEnd / ((Math.pow(1 + monthlyReturn, totalInvestmentMonths) - 1) / monthlyReturn);
      } else {
        requiredSIP = gapAtInvestmentEnd / totalInvestmentMonths;
      }
    }

    const requiredLumpsum = remainingGap > 0 ? remainingGap / Math.pow(1 + formData.expectedReturn / 100, goalTimeHorizon) : 0;

    return {
      futureValue,
      taggedAssetsFutureValue,
      remainingGap,
      requiredSIP,
      requiredLumpsum,
      coveragePercentage: futureValue > 0 ? (taggedAssetsFutureValue / futureValue) * 100 : 0
    };
  };

  const whatIfScenario = calculateWhatIfScenario();
  const projectedSIP = calculateProjectedSIP();

  const handleSubmit = (e) => {
    e.preventDefault();

    // CRITICAL: Handle Child Education with multiple children
    if (formData.name === 'Child Education' && formData.children.length > 1) {
      // Create separate goals for each child
      formData.children.forEach((child, index) => {
        const yearsToEducation = calculateYearsToEducation(child.currentClass);
        const selectedTarget = educationTargets.find(t => t.id === child.targetEducation);
        
        if (selectedTarget) {
          const childTodayCost = selectedTarget.baseExpense + (Number(child.yearlyFees) || 0);
          const savingsTargetRatio = formData.useEducationLoan
            ? (1 - (formData.educationLoanPercentage || 50) / 100)
            : 1;
          const childGoalAmount = Math.round(childTodayCost * savingsTargetRatio);

          // Generate goal name: Child Name + "Education" or just "Child Education"
          const goalName = child.name.trim() 
            ? `${child.name.trim()} - Education` 
            : `Child ${index + 1} - Education`;

          const childGoalData = {
            name: goalName,
            type: formData.type,
            timeHorizon: yearsToEducation,
            goalAmount: childGoalAmount,
            inflationRate: selectedTarget.inflationRate,
            expectedReturn: formData.expectedReturn,
            taggedAssets: index === 0 ? formData.taggedAssets : [], // Only first child gets tagged assets
            sipContribution: formData.sipContribution,
            lumpSumContribution: formData.lumpSumContribution,
            whatIfPercentage: formData.whatIfPercentage,
            useIncomePeriod: formData.useIncomePeriod,
            // Store child-specific data
            childEducationData: {
              childName: child.name,
              currentClass: child.currentClass,
              targetEducation: child.targetEducation,
              yearlyFees: child.yearlyFees,
              educationTarget: selectedTarget.name,
              useEducationLoan: formData.useEducationLoan,
              educationLoanPercentage: formData.educationLoanPercentage
            }
          };

          onSubmit(childGoalData);
        }
      });

      // Close the form after submitting all child goals
      onClose();
      return;
    }

    // For all other goal types (including single child education)
    const totalTaggedValue = formData.taggedAssets.reduce((sum, asset) => sum + (asset.amount || 0), 0);

    let finalGoalName = formData.name;

    // Handle single child education goal naming
    if (formData.name === 'Child Education' && formData.children.length === 1 && formData.children[0].name.trim()) {
      finalGoalName = `${formData.children[0].name.trim()} - Education`;
    } else if (formData.name === 'Other' && formData.customGoalName.trim()) {
      finalGoalName = formData.customGoalName.trim();
    }

    const submitData = {
      ...formData,
      name: finalGoalName,
      tagExistingInvestments: totalTaggedValue > 0,
      existingInvestmentValue: totalTaggedValue,
      selectedAsset: formData.taggedAssets.length > 0 ? formData.taggedAssets[0].assetKey : '',
      // Store child data for single child
      childEducationData: formData.name === 'Child Education' && formData.children.length === 1 ? {
        childName: formData.children[0].name,
        currentClass: formData.children[0].currentClass,
        targetEducation: formData.children[0].targetEducation,
        yearlyFees: formData.children[0].yearlyFees,
        useEducationLoan: formData.useEducationLoan,
        educationLoanPercentage: formData.educationLoanPercentage
      } : null
    };

    onSubmit(submitData);
  };

  const formatCurrency = (value) => new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0
  }).format(value);

  const educationCategories = [...new Set(educationTargets.map(target => target.category))];
  const filteredEducationTargets = educationCategory
    ? educationTargets.filter(target => target.category === educationCategory)
    : educationTargets;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-lg bg-[#19b48d] flex items-center justify-center">
              <SafeIcon icon={FiTarget} className="text-white text-sm" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">{goal ? 'Edit Goal' : 'Add New Goal'}</h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            <SafeIcon icon={FiX} className="text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* LEFT COLUMN */}
            <div className="space-y-6">
              {/* Goal Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Goal Name</label>
                <select
                  value={formData.name}
                  onChange={(e) => handleGoalTypeChange(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19b48d] focus:border-transparent"
                  required
                >
                  <option value="">Select a goal</option>
                  {goalTypes.map(type => <option key={type} value={type}>{type}</option>)}
                </select>
                {formData.name === 'Other' && (
                  <input
                    type="text"
                    placeholder="Enter custom goal name"
                    value={formData.customGoalName}
                    onChange={(e) => handleInputChange('customGoalName', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19b48d] focus:border-transparent mt-2"
                    required
                  />
                )}
              </div>

              {/* EDUCATION PLANNER */}
              {showEducationPlanner && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiBookOpen} className="text-blue-600 text-sm" />
                      <h4 className="text-sm font-medium text-blue-800">Education Planning</h4>
                    </div>
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiUsers} className="text-blue-600 text-sm" />
                      <span className="text-sm font-medium text-blue-800">Children:</span>
                      <select
                        value={formData.childCount}
                        onChange={(e) => handleChildCountChange(e.target.value)}
                        className="px-2 py-1 border border-blue-300 bg-blue-50 rounded-md focus:ring-2 focus:ring-blue-400"
                      >
                        <option value="1">1</option>
                        <option value="2">2</option>
                        <option value="3">3</option>
                      </select>
                    </div>
                  </div>

                  {/* Info box for multiple children */}
                  {formData.childCount > 1 && (
                    <div className="p-3 bg-white rounded-lg border border-blue-200">
                      <p className="text-xs text-blue-800 font-medium">
                        ℹ️ Multiple children will be added as separate goals with individual timelines and targets.
                      </p>
                    </div>
                  )}

                  {/* Current school expense */}
                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-2">Current School Expense (Annual) ₹ <span className="font-normal text-blue-500">(for reference only)</span></label>
                    <input
                      type="number"
                      value={formData.currentSchoolExpense}
                      onChange={(e) => handleInputChange('currentSchoolExpense', parseInt(e.target.value))}
                      className="w-full px-4 py-3 border border-blue-200 bg-blue-50 rounded-lg focus:ring-2 focus:ring-blue-400"
                      min="0"
                    />
                    <p className="text-xs text-blue-500 mt-1">
                      This is your ongoing, current-year school fee — it's paid out of today's income, so it does not change the SIP target below. Only the future higher-education cost is what you're saving for here.
                    </p>
                  </div>

                  {/* Education Category */}
                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-2">Education Category</label>
                    <select
                      value={educationCategory}
                      onChange={(e) => handleEducationCategoryChange(e.target.value)}
                      className="w-full px-4 py-3 border border-blue-200 bg-blue-50 rounded-lg focus:ring-2 focus:ring-blue-400"
                    >
                      {educationCategories.map(category => <option key={category} value={category}>{category}</option>)}
                    </select>
                  </div>

                  {/* Per-child config */}
                  {formData.children.map((child, index) => {
                    const selectedTarget = educationTargets.find(t => t.id === child.targetEducation);
                    const yearsToEdu = calculateYearsToEducation(child.currentClass);
                    const educationCostToday = (selectedTarget?.baseExpense || 0) + (Number(child.yearlyFees) || 0);

                    return (
                      <div key={index} className="p-3 bg-white rounded-lg border border-blue-100">
                        <div className="flex items-center space-x-2 mb-3">
                          <SafeIcon icon={FiUser} className="text-blue-600 text-sm" />
                          <h5 className="text-sm font-medium text-blue-800">
                            Child {index + 1} {child.name ? `- ${child.name}` : ''}
                          </h5>
                        </div>

                        <div className="space-y-3">
                          {/* Child name */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Child's Name (Optional)</label>
                            <input
                              type="text"
                              value={child.name}
                              onChange={(e) => handleChildDataChange(index, 'name', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                              placeholder="Enter name (will be used in goal name)"
                            />
                            <p className="text-xs text-blue-600 mt-1">
                              {child.name.trim() 
                                ? `Goal will be saved as: "${child.name.trim()} - Education"` 
                                : formData.childCount > 1 
                                  ? `Goal will be saved as: "Child ${index + 1} - Education"` 
                                  : 'Goal will be saved as: "Child Education"'}
                            </p>
                          </div>

                          {/* Current class */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Current Class</label>
                            <select
                              value={child.currentClass}
                              onChange={(e) => handleChildDataChange(index, 'currentClass', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                              required
                            >
                              {educationClasses.map(cls => <option key={cls} value={cls}>{cls}</option>)}
                            </select>
                            <p className="text-xs text-blue-600 mt-1">Years until higher education: {yearsToEdu}</p>
                          </div>

                          {/* Target education */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Target Education</label>
                            <select
                              value={child.targetEducation}
                              onChange={(e) => handleChildDataChange(index, 'targetEducation', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                              required
                            >
                              {filteredEducationTargets.map(target => (
                                <option key={target.id} value={target.id}>
                                  {target.name} - {target.duration} (~₹{(target.baseExpense / 100000).toFixed(1)}L today)
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Additional yearly fees */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Additional Yearly Expenses ₹ (Optional)</label>
                            <input
                              type="number"
                              value={child.yearlyFees}
                              onChange={(e) => handleChildDataChange(index, 'yearlyFees', parseInt(e.target.value) || 0)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                              min="0"
                            />
                            <p className="text-xs text-gray-500 mt-1">For coaching, books, equipment, etc.</p>
                          </div>

                          {/* Cost Display */}
                          <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100 space-y-1">
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-gray-600 font-medium">Education Cost (Today):</span>
                              <span className="font-bold text-blue-900">{formatCurrency(educationCostToday)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* Education Loan Option */}
                  <div className="bg-white p-4 rounded-lg border border-blue-200 mt-4">
                    <h5 className="text-sm font-medium text-blue-800 mb-3">SIP Funding Strategy</h5>
                    <div className="space-y-3">
                      <label className="flex items-start space-x-3 cursor-pointer p-2 rounded hover:bg-gray-50 transition-colors">
                        <input
                          type="radio"
                          name="fundingStrategy"
                          checked={!formData.useEducationLoan}
                          onChange={() => handleInputChange('useEducationLoan', false)}
                          className="mt-1 text-blue-600 focus:ring-blue-500"
                        />
                        <div>
                          <span className="block text-sm font-medium text-gray-900">100% Savings (No Loan)</span>
                          <span className="block text-xs text-gray-500">Calculate SIP for the entire education cost.</span>
                        </div>
                      </label>

                      <label className="flex items-start space-x-3 cursor-pointer p-2 rounded hover:bg-gray-50 transition-colors">
                        <input
                          type="radio"
                          name="fundingStrategy"
                          checked={formData.useEducationLoan}
                          onChange={() => handleInputChange('useEducationLoan', true)}
                          className="mt-1 text-blue-600 focus:ring-blue-500"
                        />
                        <div>
                          <span className="block text-sm font-medium text-gray-900">Partial Education Loan</span>
                          <span className="block text-xs text-gray-500">Take a loan for a portion, save for the rest.</span>
                        </div>
                      </label>
                    </div>

                    {formData.useEducationLoan && (
                      <div className="mt-4 pl-8 pr-2">
                        <div className="flex justify-between mb-2">
                          <label className="text-xs font-medium text-gray-700">Loan Percentage</label>
                          <span className="text-xs font-bold text-blue-700 bg-blue-100 px-2 py-0.5 rounded">
                            {formData.educationLoanPercentage}%
                          </span>
                        </div>
                        <input
                          type="range"
                          min="10"
                          max="90"
                          step="5"
                          value={formData.educationLoanPercentage}
                          onChange={(e) => handleInputChange('educationLoanPercentage', parseInt(e.target.value))}
                          className="w-full accent-blue-600"
                        />
                        <div className="flex justify-between text-[10px] text-gray-500 mt-1 px-1">
                          <span>10%</span>
                          <span>90%</span>
                        </div>
                        <p className="text-[11px] text-orange-700 bg-orange-50 border border-orange-100 rounded-lg p-2 mt-3">
                          Note: this only reduces the amount you need to save via SIP. It does not account for the interest you'll pay on the loan itself — factor that in separately when comparing loan vs. full self-funding.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Time Horizon Insight */}
                  <div className="bg-white p-3 rounded-lg mt-2">
                    <div className="flex items-center space-x-2 mb-1">
                      <SafeIcon icon={FiCalendar} className="text-blue-600 text-xs" />
                      <span className="text-xs font-medium text-blue-800">Time Horizon</span>
                    </div>
                    <p className="text-xs text-blue-700">
                      Planning for education costs {goalTimeHorizon} years from now with an average inflation rate of {Number(formData.inflationRate).toFixed(1)}%.
                    </p>
                  </div>
                </div>
              )}

              {/* Goal Type & Time Horizon */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Goal Type</label>
                  <select
                    value={formData.type}
                    onChange={(e) => handleInputChange('type', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19b48d]"
                    required
                  >
                    <option value="Lump Sum">Lump Sum</option>
                    <option value="Recurring">Recurring</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Time Horizon (Years)</label>
                  <input
                    type="number"
                    value={formData.timeHorizon}
                    onChange={(e) => handleInputChange('timeHorizon', parseInt(e.target.value))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19b48d]"
                    min="1"
                    max="50"
                    required
                  />
                </div>
              </div>

              {/* Use income period checkbox */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="useIncomePeriod"
                    checked={formData.useIncomePeriod}
                    onChange={(e) => handleInputChange('useIncomePeriod', e.target.checked)}
                    className="w-4 h-4 text-[#19b48d] border-gray-300 rounded"
                  />
                  <label htmlFor="useIncomePeriod" className="text-sm font-medium text-blue-800 cursor-pointer">
                    Use Active Income Period for Investment
                  </label>
                </div>
                <div className="mt-2 text-xs text-blue-700">
                  {formData.useIncomePeriod ? (
                    <>
                      <div className="flex items-center space-x-2 mb-1">
                        <SafeIcon icon={FiClock} className="text-blue-600" />
                        <span className="font-medium">Investment Period: {investmentHorizon} years</span>
                      </div>
                      <p>You will invest for {investmentHorizon} years. The corpus will grow until the {goalTimeHorizon}th year.</p>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center space-x-2 mb-1">
                        <SafeIcon icon={FiClock} className="text-blue-600" />
                        <span className="font-medium">Investment Period: {goalTimeHorizon} years</span>
                      </div>
                      <p>You will invest for the specified time horizon of {goalTimeHorizon} years.</p>
                    </>
                  )}
                </div>
              </div>

              {/* Goal Amount (non-education) */}
              {!showEducationPlanner && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Goal Amount (Today's Value) ₹</label>
                  <input
                    type="number"
                    value={formData.goalAmount}
                    onChange={(e) => handleInputChange('goalAmount', parseInt(e.target.value))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19b48d]"
                    min="1000"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">{formatCurrency(formData.goalAmount)}</p>
                </div>
              )}

              {/* Inflation & Return */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Inflation Rate (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.inflationRate}
                    onChange={(e) => handleInputChange('inflationRate', parseFloat(e.target.value))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19b48d]"
                    min="0"
                    max="50"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Expected Return (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.expectedReturn}
                    onChange={(e) => handleInputChange('expectedReturn', parseFloat(e.target.value))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19b48d]"
                    min="0"
                    max="50"
                    required
                  />
                </div>
              </div>

              {/* Future Value Preview */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-1">Savings Target Preview</h4>
                {showEducationPlanner && (
                  <p className="text-xs text-blue-600 mb-2">
                    Based on today's target of {formatCurrency(formData.goalAmount)} inflated at {Number(formData.inflationRate).toFixed(1)}%
                  </p>
                )}
                <p className="text-lg font-semibold text-blue-800">
                  {formatCurrency(formData.goalAmount * Math.pow(1 + formData.inflationRate / 100, goalTimeHorizon))}
                </p>
                <p className="text-xs text-gray-600">Required in {goalTimeHorizon} years</p>
              </div>

              {/* What-If */}
              <div className="border-t pt-6">
                <div className="flex items-center space-x-2 mb-4">
                  <SafeIcon icon={FiPercent} className="text-[#f58320] text-sm" />
                  <h4 className="text-sm font-medium text-gray-700">What If Scenario</h4>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">If goal cost increases by</label>
                  <div className="grid grid-cols-5 gap-2">
                    {whatIfPercentageOptions.map(percent => (
                      <button
                        key={percent}
                        type="button"
                        className={`py-2 px-3 rounded-lg text-sm font-medium border ${
                          formData.whatIfPercentage === percent
                            ? 'bg-[#f58320] text-white border-[#f58320]'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                        onClick={() => handleInputChange('whatIfPercentage', percent)}
                      >
                        {percent}%
                      </button>
                    ))}
                  </div>
                </div>

                <div className="bg-orange-50 rounded-lg p-4 mt-4">
                  <h5 className="text-sm font-medium text-gray-700 mb-2">What If Analysis</h5>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Original Future Value:</span>
                      <span className="font-medium text-gray-800">{formatCurrency(whatIfScenario.standardFutureValue)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Increased Future Value:</span>
                      <span className="font-medium text-orange-800">{formatCurrency(whatIfScenario.whatIfFutureValue)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Additional Amount:</span>
                      <span className="font-medium text-orange-800">{formatCurrency(whatIfScenario.additionalAmount)}</span>
                    </div>
                    <div className="flex justify-between text-sm border-t border-orange-200 pt-2 mt-2">
                      <span className="text-gray-700">What-If SIP Required:</span>
                      <span className="font-medium text-orange-800">{formatCurrency(Math.round(whatIfScenario.whatIfSIP))}/month</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">What-If Lumpsum Required:</span>
                      <span className="font-medium text-orange-800">{formatCurrency(Math.round(whatIfScenario.whatIfLumpsum))}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* MIDDLE COLUMN: Asset Tagging */}
            <div className="border-t pt-6 lg:border-t-0 lg:pt-0">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-medium text-gray-700">Tag Existing Assets (Up to 3)</h4>
                {formData.taggedAssets.length < 3 && (
                  <button
                    type="button"
                    onClick={addTaggedAsset}
                    className="flex items-center space-x-1 text-sm text-[#19b48d] hover:text-[#16a085]"
                  >
                    <SafeIcon icon={FiPlus} className="text-xs" />
                    <span>Add Asset</span>
                  </button>
                )}
              </div>

              <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center space-x-2 mb-2">
                  <SafeIcon icon={FiInfo} className="text-blue-600 text-sm" />
                  <span className="text-sm font-medium text-blue-800">Available Assets from Profile</span>
                </div>
                <p className="font-bold text-sm text-blue-700">{formatCurrency(totalAvailableAssets)}</p>
                <p className="text-xs text-blue-600 mt-1">Total untagged assets available for this goal</p>
              </div>

              <div className="space-y-4">
                {formData.taggedAssets.map((taggedAsset, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-gray-700">Asset {index + 1}</span>
                      <button
                        type="button"
                        onClick={() => removeTaggedAsset(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <SafeIcon icon={FiMinus} className="text-xs" />
                      </button>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Select Asset</label>
                        <select
                          value={taggedAsset.assetKey}
                          onChange={(e) => handleAssetSelection(index, e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          <option value="">Choose an asset</option>
                          {assetOptions.map(asset => (
                            <option key={asset.value} value={asset.value}>
                              {asset.label} - {formatCurrency(asset.remaining)} available
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Amount to Tag ₹</label>
                        <input
                          type="number"
                          value={taggedAsset.amount}
                          onChange={(e) => updateTaggedAsset(index, 'amount', parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          min="0"
                        />
                        <p className="text-xs text-gray-500 mt-1">{formatCurrency(taggedAsset.amount || 0)}</p>
                      </div>

                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Expected ROI (%)</label>
                        <input
                          type="number"
                          step="0.1"
                          value={taggedAsset.expectedReturn}
                          onChange={(e) => updateTaggedAsset(index, 'expectedReturn', parseFloat(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          min="0"
                          max="50"
                        />
                      </div>

                      {taggedAsset.assetKey && taggedAsset.expectedReturn > 0 && (
                        <div className="p-2 bg-blue-50 rounded-lg">
                          <p className="text-xs text-blue-800">
                            <strong>Future Value:</strong>{' '}
                            {formatCurrency(taggedAsset.amount * Math.pow(1 + taggedAsset.expectedReturn / 100, goalTimeHorizon))}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {formData.taggedAssets.length > 0 && (
                <>
                  <div className="mt-4 p-3 bg-green-50 rounded-lg">
                    <p className="text-sm text-green-800">
                      <strong>Total Tagged:</strong>{' '}
                      {formatCurrency(formData.taggedAssets.reduce((sum, asset) => sum + (asset.amount || 0), 0))}
                    </p>
                  </div>

                  <div className="mt-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                    <h4 className="text-sm font-semibold text-green-900 mb-3">Projected Investment Required</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-green-700">Goal Future Value:</span>
                        <span className="text-sm font-bold text-green-900">{formatCurrency(projectedSIP.futureValue)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-green-700">Tagged Assets Future Value:</span>
                        <span className="text-sm font-bold text-green-900">{formatCurrency(projectedSIP.taggedAssetsFutureValue)}</span>
                      </div>
                      <div className="w-full bg-white rounded-full h-2 mb-2">
                        <div
                          className="h-2 rounded-full bg-gradient-to-r from-green-500 to-emerald-600"
                          style={{ width: `${Math.min(projectedSIP.coveragePercentage, 100)}%` }}
                        />
                      </div>
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-green-700">Coverage:</span>
                        <span className="font-bold text-green-900">{projectedSIP.coveragePercentage.toFixed(1)}%</span>
                      </div>
                      <div className="border-t border-green-200 pt-3 mt-3">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-green-700">Remaining Gap:</span>
                          <span className="text-sm font-bold text-orange-700">{formatCurrency(projectedSIP.remainingGap)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-green-800">Required Monthly SIP:</span>
                          <span className="text-lg font-bold text-green-900">{formatCurrency(projectedSIP.requiredSIP)}</span>
                        </div>
                        <div className="flex justify-between items-center mt-2">
                          <span className="text-xs text-green-700">OR Lumpsum Required:</span>
                          <span className="text-sm font-bold text-green-900">{formatCurrency(projectedSIP.requiredLumpsum)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Additional contributions */}
              <div className="border-t pt-6 mt-6">
                <h4 className="text-sm font-medium text-gray-700 mb-4">Additional Contributions (Optional)</h4>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Monthly SIP ₹</label>
                    <input
                      type="number"
                      value={formData.sipContribution}
                      onChange={(e) => handleInputChange('sipContribution', parseInt(e.target.value))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19b48d]"
                      min="0"
                    />
                    <p className="text-xs text-gray-500 mt-1">{formatCurrency(formData.sipContribution)}/month</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">One-time Lumpsum ₹</label>
                    <input
                      type="number"
                      value={formData.lumpSumContribution}
                      onChange={(e) => handleInputChange('lumpSumContribution', parseInt(e.target.value))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19b48d]"
                      min="0"
                    />
                    <p className="text-xs text-gray-500 mt-1">{formatCurrency(formData.lumpSumContribution)} today</p>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN: Projections */}
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-[#19b48d]/10 to-[#19b48d]/5 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Future Value Projections</h4>
                <div className="space-y-4">
                  <div className="bg-white rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Standard Scenario</span>
                      <span className="text-sm font-medium text-[#19b48d]">{Number(formData.inflationRate).toFixed(1)}% inflation</span>
                    </div>
                    {showEducationPlanner && (
                      <p className="text-xs text-blue-600 mt-1">Today's target cost: {formatCurrency(formData.goalAmount)}</p>
                    )}
                    <p className="text-xl font-bold text-gray-900 mt-1">
                      {formatCurrency(whatIfScenario.standardFutureValue)}
                    </p>
                    <p className="text-xs text-gray-600">Required in {goalTimeHorizon} years</p>
                  </div>

                  <div className="bg-white rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">What-If Scenario</span>
                      <span className="text-sm font-medium text-orange-600">+{formData.whatIfPercentage}% increase</span>
                    </div>
                    <p className="text-xl font-bold text-orange-700 mt-1">
                      {formatCurrency(whatIfScenario.whatIfFutureValue)}
                    </p>
                    <div className="flex justify-between text-xs mt-1">
                      <span className="text-gray-600">Additional requirement:</span>
                      <span className="text-orange-600 font-medium">{formatCurrency(whatIfScenario.additionalAmount)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Education insights */}
              {showEducationPlanner && (
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <SafeIcon icon={FiAward} className="text-purple-600" />
                    <h4 className="text-lg font-semibold text-gray-900">Education Insights</h4>
                  </div>
                  <div className="space-y-4">
                    <div className="bg-white rounded-lg p-4">
                      <h5 className="text-sm font-medium text-gray-800 mb-3">Funding Breakdown (Future Value)</h5>
                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Total Education Cost:</span>
                          <span className="font-medium text-gray-900">
                            {formatCurrency(formData.educationTodayCost * Math.pow(1 + formData.inflationRate / 100, goalTimeHorizon))}
                          </span>
                        </div>
                        {formData.useEducationLoan && (
                          <div className="flex justify-between items-center bg-orange-50 p-2 rounded">
                            <span className="text-orange-800">Education Loan ({formData.educationLoanPercentage}%):</span>
                            <span className="font-bold text-orange-700">
                              {formatCurrency(
                                (formData.educationTodayCost * Math.pow(1 + formData.inflationRate / 100, goalTimeHorizon)) *
                                (formData.educationLoanPercentage / 100)
                              )}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between items-center bg-green-50 p-2 rounded">
                          <span className="text-green-800 font-medium">
                            SIP Target ({formData.useEducationLoan ? 100 - formData.educationLoanPercentage : 100}%):
                          </span>
                          <span className="font-bold text-green-700">{formatCurrency(whatIfScenario.standardFutureValue)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-4 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-[#19b48d] text-white rounded-lg hover:bg-[#16a085] transition-colors duration-200"
            >
              {goal ? 'Update Goal' : formData.name === 'Child Education' && formData.childCount > 1 ? `Add ${formData.childCount} Goals` : 'Add Goal'}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

export default GoalForm;